import { ignoredFormKeyCodes } from "../../ignored_form_key_codes";

const CC_ED_M_MAX_LENGTH = 2;
const CC_ED_Y_MAX_LENGTH = 2;
const FORMATTED_CC_ED_MAX_LENGTH = CC_ED_M_MAX_LENGTH + CC_ED_Y_MAX_LENGTH + 1;
const NUM_KEYS = new Array(10).fill(null).map((_, i) => i.toString());
const ALLOWED_KEYS_NUMERIC = [...NUM_KEYS, ...ignoredFormKeyCodes];

export function replaceStringDate (value) {
  return value
    .replace(/^([1-9]\/|[2-9])$/g, "0$1/") // 3 > 03/
    .replace(/^(0[1-9]|1[0-2])$/g, "$1/") // 11 > 11/
    .replace(/^([0-1])([3-9])$/g, "0$1/$2") // 13 > 01/3
    .replace(/^(0?[1-9]|1[0-2])([0-9]{2})$/g, "$1/$2") // 141 > 01/41
    .replace(/^([0]+)\/|[0]+$/g, "0") // 0/ > 0 and 00 > 0
    .replace(/[^\d/]|^[/]*$/g, "") // To allow only digits and `/`
    .replace(/\/\//g, "/"); // Prevent entering more than 1 `/`
}

export function ccEDExtractor (value) {
  const [month, year] = value.split("/");

  if (year.length && year.length < 3) {
    const currentYearHundreds = new Date().getFullYear().toFixed(0).slice(0, CC_ED_Y_MAX_LENGTH);
    return [
      month,
      currentYearHundreds + year.padStart(2, "0")
    ].join("/");
  }
  return value;
}

export function ccEDFormatter (value) {
  const [month, year] = value.slice(0, FORMATTED_CC_ED_MAX_LENGTH).split("/");
  if (Number.isNaN(+month)) {
    return "";
  }
  const numMonth = +month > 12 || +month < 1 ? 1 : Number(month);
  const strMonth = numMonth.toString().padStart(CC_ED_M_MAX_LENGTH, "0");

  if (year?.length && year.length > 2) {
    const twoDigitsYear = year.slice(CC_ED_Y_MAX_LENGTH).padStart(CC_ED_Y_MAX_LENGTH, "0");
    return [
      strMonth,
      twoDigitsYear
    ].join("/");
  }
  return [strMonth, year].join("/");
}

export function ccEDValidator (value) {
  const [month, year] = value.split("/");
  const currentYear = new Date().getFullYear();
  const currentYearHundreds = currentYear.toFixed(0).slice(0, CC_ED_Y_MAX_LENGTH);

  if (year.length === 3 || Number.isNaN(year) || +year < 1) {
    return false;
  }

  const fullYear = Number(
    year.length < 4
      ? currentYearHundreds + year.toFixed(0).padStart(2, "0")
      : year.toFixed(0)
  );

  if (fullYear < currentYear) {
    return false;
  }

  const numMonth = Number(month);
  if (!Number.isNaN(numMonth) || numMonth < 1 || numMonth > 12) {
    return false;
  }
  if (fullYear > currentYear) {
    return true;
  }

  const currentMonth = new Date().getMonth() + 1;

  return numMonth >= currentMonth;
}

export function ccEDKeydownHandler (event) {
  if (event.key == 'Enter') {
    event.stopPropagation();
    event.preventDefault();
    return;
  }
  if (![event.metaKey, event.altKey, event.ctrlKey].some(active => active)) {
    if (!ALLOWED_KEYS_NUMERIC.includes(event.key) && !ignoredFormKeyCodes.includes(event.keyCode)) {
      event.preventDefault();
      return;
    }
    if (ignoredFormKeyCodes.includes(event.keyCode)) {
      return;
    }
  }

  const slicedValue = event.target.value.slice(0, FORMATTED_CC_ED_MAX_LENGTH);

  event.target.value = replaceStringDate(slicedValue);
}

export function ccEDKeyupHandler (event) {
  const { selectionStart, selectionEnd } = event.target;
  const shouldFocusNext =
    !ignoredFormKeyCodes.includes(event.keyCode) &&
    event.target.value.length >= 5 &&
    selectionStart >= 5 &&
    selectionEnd >= 5;

  event.target.value = event.target.value.slice(0, FORMATTED_CC_ED_MAX_LENGTH);

  if (shouldFocusNext) {
    event.target.nextElementSibling.focus();
  }
}

export function ccEDPasteHandler (event) {
  const pasteData = event.clipboardData.getData("text");
  event.preventDefault(pasteData);
  event.target.value = replaceStringDate(pasteData);
}

export function setupCCExpirationDateInput (inputElement) {
  inputElement.addEventListener("keydown", ccEDKeydownHandler);
  inputElement.addEventListener("keyup", ccEDKeyupHandler);
  inputElement.addEventListener("paste", ccEDPasteHandler);

  inputElement.setAttribute("maxlength", FORMATTED_CC_ED_MAX_LENGTH);
  inputElement.setAttribute("pattern", `^[0-9]{${CC_ED_M_MAX_LENGTH}}/[0-9]{${CC_ED_Y_MAX_LENGTH}}$`);

  return new Proxy(inputElement, {
    get (target, prop, _receiver) {
      switch (prop) {
        case "value":
          return ccEDExtractor(target.value ?? "");
        case "_self":
          return target;
        default:
          return Reflect.get(...arguments);
      }
    },
    set (target, prop, value, _receiver) {
      if (prop === "value") {
        target.value = replaceStringDate(ccEDFormatter(value));
      } else {
        return Reflect.set(...arguments);
      }
      return true;
    }
  });
}
